import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SearchIcon from "@mui/icons-material/Search";
import moment from "moment";
import * as XLSX from "xlsx";
import { UrlApi } from "../../services/apirest";
import { logEventos } from "../../services/services";

const ESTADOS = [
    { id: 229, nombre: "Asignado" },
    { id: 230, nombre: "Yendo a Petrolera" },
    { id: 231, nombre: "Llegó a Petrolera" },
    { id: 232, nombre: "Yendo a Destino" },
    { id: 233, nombre: "Finalizado" },
];

const PRODUCTOS = [
    { id: "butano", nombre: "Butano" },
    { id: "propano", nombre: "Propano" },
    { id: "bso", nombre: "BSO" },
    { id: "propelente", nombre: "Propelente" },
];

const EMPRESAS = [
    { id: 1, nombre: "Hipergas" },
    { id: 4, nombre: "NaturalGas" },
];

const COLUMNAS_EXCEL = [
    { titulo: "ID", campo: "id_viaje", ancho: 8 },
    { titulo: "Salida", campo: "fecha_salida", ancho: 12, fecha: true },
    { titulo: "Llegada", campo: "fecha_llegada", ancho: 12, fecha: true },
    { titulo: "Producto", campo: "producto", ancho: 12 },
    { titulo: "Proveedor", campo: "proveedor", ancho: 16 },
    { titulo: "Ubicación proveedor", campo: "ubicacion_proveedor", ancho: 22 },
    { titulo: "Empresa", campo: "empresa", ancho: 14 },
    { titulo: "Destino", campo: "destino", ancho: 22 },
    { titulo: "Destino anterior", campo: "destino_anterior", ancho: 22 },
    { titulo: "Situación", campo: "situacion", ancho: 24 },
    { titulo: "Transportista", campo: "transportista", ancho: 24 },
    { titulo: "Vehículo", campo: "vehiculo", ancho: 12 },
    { titulo: "Acoplados", campo: "acoplados", ancho: 26 },
    { titulo: "Estado", campo: "estado", ancho: 18 },
    { titulo: "Punto de venta", campo: "punto_venta", ancho: 14 },
    { titulo: "Remito", campo: "remito", ancho: 14 },
    { titulo: "Km inicial", campo: "kilometraje_inicial", ancho: 12 },
    { titulo: "Km final", campo: "kilometraje_final", ancho: 12 },
    { titulo: "Usuario", campo: "usuario", ancho: 18 },
];

const COLUMNAS_VISTA = [
    { titulo: "ID", campo: "id_viaje" },
    { titulo: "Salida", campo: "fecha_salida" },
    { titulo: "Llegada", campo: "fecha_llegada" },
    { titulo: "Producto", campo: "producto" },
    { titulo: "Proveedor", campo: "proveedor" },
    { titulo: "Empresa", campo: "empresa" },
    { titulo: "Destino", campo: "destino" },
    { titulo: "Situación", campo: "situacion" },
    { titulo: "Transportista", campo: "transportista" },
    { titulo: "Vehículo", campo: "vehiculo" },
    { titulo: "Estado", campo: "estado" },
    { titulo: "Remito", campo: "remito" },
];

const FILAS_VISTA = 100;

const crearFiltrosIniciales = () => ({
    campo_fecha: "salida",
    desde: moment().startOf("month").format("YYYY-MM-DD"),
    hasta: moment().endOf("month").format("YYYY-MM-DD"),
    sucursal: null,
    estado: "",
    producto: "",
    empresa: "",
    proveedor: null,
    transportista: null,
    solo_derivados: false,
});

const fechaACelda = (valor) => {
    if (!valor) return { t: "s", v: "" };

    const [anio, mes, dia] = valor.split("-").map(Number);
    const serial =
        (Date.UTC(anio, mes - 1, dia) - Date.UTC(1899, 11, 30)) / 86400000;

    return { t: "n", v: serial, z: "dd/mm/yyyy" };
};

function DialogReporteViajes({ abrir, onClose }) {
    const sucursalesAutorizadas = localStorage.getItem("suc_auth") || "";

    const [filtros, setFiltros] = useState(crearFiltrosIniciales);
    const [sucursales, setSucursales] = useState([]);
    const [proveedores, setProveedores] = useState([]);
    const [transportistas, setTransportistas] = useState([]);
    const [filas, setFilas] = useState(null);
    const [truncado, setTruncado] = useState(false);
    const [cargando, setCargando] = useState(false);
    const [error, setError] = useState("");
    const buscandoRef = useRef(false);

    useEffect(() => {
        let vigente = true;

        const pedirLista = async (ruta) => {
            const res = await fetch(UrlApi + ruta);
            const data = await res.json().catch(() => null);

            if (!res.ok || !Array.isArray(data)) {
                throw new Error("Respuesta inválida en " + ruta);
            }

            return data;
        };

        Promise.all([
            pedirLista("/sucursales/tipoPlanta"),
            pedirLista("/bocas_carga"),
            pedirLista("/transportistas"),
        ])
            .then(([listaSucursales, listaProveedores, listaTransportistas]) => {
                if (!vigente) return;

                const autorizadas = sucursalesAutorizadas
                    .split(",")
                    .map((id) => id.trim());

                setSucursales(
                    listaSucursales.filter((sucursal) =>
                        autorizadas.includes(String(sucursal.id))
                    )
                );
                setProveedores(listaProveedores);
                setTransportistas(listaTransportistas);
            })
            .catch((err) => {
                console.error(err);
                if (vigente) setError("No se pudieron cargar las opciones de los filtros.");
            });

        return () => {
            vigente = false;
        };
    }, [sucursalesAutorizadas]);

    const cambiarFiltro = (campo, valor) => {
        setFiltros((anteriores) => ({ ...anteriores, [campo]: valor }));
        setFilas(null);
        setTruncado(false);
        setError("");
    };

    const limpiarFiltros = () => {
        setFiltros(crearFiltrosIniciales());
        setFilas(null);
        setTruncado(false);
        setError("");
    };

    const buscar = async () => {
        if (buscandoRef.current) return;

        buscandoRef.current = true;
        setCargando(true);
        setError("");

        try {
            const res = await fetch(UrlApi + "/viajes_abastecimiento/reporte", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    sucursales_autorizadas: sucursalesAutorizadas,
                    campo_fecha: filtros.campo_fecha,
                    desde: filtros.desde,
                    hasta: filtros.hasta,
                    sucursal: filtros.sucursal?.id ?? "",
                    estado: filtros.estado,
                    producto: filtros.producto,
                    empresa: filtros.empresa,
                    proveedor: filtros.proveedor?.idbocas_carga ?? "",
                    transportista: filtros.transportista?.id_transportista ?? "",
                    solo_derivados: filtros.solo_derivados,
                }),
            });

            const data = await res.json().catch(() => null);

            if (!res.ok || !data?.ok || !Array.isArray(data.filas)) {
                setFilas(null);
                setError(data?.mensaje || "No se pudo generar el reporte.");
                return;
            }

            setFilas(data.filas);
            setTruncado(Boolean(data.truncado));
        } catch (err) {
            console.error(err);
            setFilas(null);
            setError("No se pudo conectar con el servidor. Intentá nuevamente.");
        } finally {
            buscandoRef.current = false;
            setCargando(false);
        }
    };

    const descargarExcel = () => {
        if (!filas || filas.length === 0) return;

        const encabezados = COLUMNAS_EXCEL.map((columna) => columna.titulo);
        const datos = filas.map((fila) =>
            COLUMNAS_EXCEL.map((columna) =>
                columna.fecha ? fechaACelda(fila[columna.campo]) : fila[columna.campo] ?? ""
            )
        );

        const hoja = XLSX.utils.aoa_to_sheet([encabezados, ...datos]);

        hoja["!cols"] = COLUMNAS_EXCEL.map((columna) => ({ wch: columna.ancho }));
        hoja["!autofilter"] = {
            ref: XLSX.utils.encode_range({
                s: { r: 0, c: 0 },
                e: { r: filas.length, c: COLUMNAS_EXCEL.length - 1 },
            }),
        };

        const libro = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(libro, hoja, "Viajes");
        XLSX.writeFile(
            libro,
            `reporte_viajes_${filtros.desde}_a_${filtros.hasta}.xlsx`
        );

        logEventos(
            "Descargar reporte de viajes",
            "Viajes a Sucursales",
            `Se descargó el reporte del ${filtros.desde} al ${filtros.hasta} (${filas.length} viajes)`,
            localStorage.getItem("user")
        );
    };

    const filasVista = useMemo(
        () => (filas ? filas.slice(0, FILAS_VISTA) : []),
        [filas]
    );

    return (
        <Dialog open={abrir} onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle sx={{ pb: 1, fontSize: 17, fontWeight: 700 }}>
                Reporte de viajes
            </DialogTitle>

            <DialogContent dividers>
                <Box
                    sx={{
                        bgcolor: "#E0F2F1",
                        borderTop: "3px solid #00796B",
                        borderRadius: 1,
                        p: 1.25,
                        "& .MuiOutlinedInput-root": { bgcolor: "white" },
                    }}
                >
                    <Grid container spacing={1} alignItems="center">
                        <Grid item xs={12}>
                            <FormControl>
                                <RadioGroup
                                    row
                                    value={filtros.campo_fecha}
                                    onChange={(e) => cambiarFiltro("campo_fecha", e.target.value)}
                                >
                                    <FormControlLabel
                                        value="salida"
                                        control={<Radio size="small" />}
                                        label="Fecha de salida"
                                    />
                                    <FormControlLabel
                                        value="llegada"
                                        control={<Radio size="small" />}
                                        label="Fecha de llegada"
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Grid>

                        <Grid item xs={6} md={3}>
                            <TextField
                                fullWidth
                                required
                                size="small"
                                type="date"
                                label="Desde"
                                value={filtros.desde}
                                onChange={(e) => cambiarFiltro("desde", e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <TextField
                                fullWidth
                                required
                                size="small"
                                type="date"
                                label="Hasta"
                                value={filtros.hasta}
                                onChange={(e) => cambiarFiltro("hasta", e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Autocomplete
                                size="small"
                                options={sucursales}
                                value={filtros.sucursal}
                                autoHighlight
                                noOptionsText="Sin sucursales autorizadas"
                                isOptionEqualToValue={(opcion, valor) => opcion.id === valor.id}
                                getOptionLabel={(sucursal) => sucursal.nombre}
                                onChange={(event, valor) => cambiarFiltro("sucursal", valor)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Sucursal"
                                        placeholder="Todas mis sucursales"
                                        InputLabelProps={{ shrink: true }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel shrink>Estado</InputLabel>
                                <Select
                                    notched
                                    displayEmpty
                                    label="Estado"
                                    value={filtros.estado}
                                    onChange={(e) => cambiarFiltro("estado", e.target.value)}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    {ESTADOS.map((estado) => (
                                        <MenuItem key={estado.id} value={estado.id}>
                                            {estado.nombre}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel shrink>Producto</InputLabel>
                                <Select
                                    notched
                                    displayEmpty
                                    label="Producto"
                                    value={filtros.producto}
                                    onChange={(e) => cambiarFiltro("producto", e.target.value)}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    {PRODUCTOS.map((producto) => (
                                        <MenuItem key={producto.id} value={producto.id}>
                                            {producto.nombre}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel shrink>Empresa</InputLabel>
                                <Select
                                    notched
                                    displayEmpty
                                    label="Empresa"
                                    value={filtros.empresa}
                                    onChange={(e) => cambiarFiltro("empresa", e.target.value)}
                                >
                                    <MenuItem value="">Todas</MenuItem>
                                    {EMPRESAS.map((empresa) => (
                                        <MenuItem key={empresa.id} value={empresa.id}>
                                            {empresa.nombre}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Autocomplete
                                size="small"
                                options={proveedores}
                                value={filtros.proveedor}
                                autoHighlight
                                noOptionsText="Proveedor inexistente"
                                isOptionEqualToValue={(opcion, valor) =>
                                    opcion.idbocas_carga === valor.idbocas_carga
                                }
                                getOptionLabel={(proveedor) =>
                                    proveedor.proveedor + " - " + proveedor.nombre_boca
                                }
                                renderOption={(props, proveedor) => (
                                    <li {...props} key={proveedor.idbocas_carga}>
                                        {proveedor.proveedor} - {proveedor.nombre_boca}
                                    </li>
                                )}
                                onChange={(event, valor) => cambiarFiltro("proveedor", valor)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Proveedor"
                                        placeholder="Todos"
                                        InputLabelProps={{ shrink: true }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Autocomplete
                                size="small"
                                options={transportistas}
                                value={filtros.transportista}
                                autoHighlight
                                noOptionsText="Transportista inexistente"
                                isOptionEqualToValue={(opcion, valor) =>
                                    opcion.id_transportista === valor.id_transportista
                                }
                                getOptionLabel={(transportista) => transportista.nombre}
                                renderOption={(props, transportista) => (
                                    <li {...props} key={transportista.id_transportista}>
                                        {transportista.nombre}
                                    </li>
                                )}
                                onChange={(event, valor) =>
                                    cambiarFiltro("transportista", valor)
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Transportista"
                                        placeholder="Todos"
                                        InputLabelProps={{ shrink: true }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControlLabel
                                label="Solo viajes derivados"
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={filtros.solo_derivados}
                                        onChange={(e) =>
                                            cambiarFiltro("solo_derivados", e.target.checked)
                                        }
                                    />
                                }
                            />
                        </Grid>

                        <Grid
                            item
                            xs={12}
                            md={9}
                            sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
                        >
                            <Button size="small" onClick={limpiarFiltros} disabled={cargando}>
                                Limpiar
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                startIcon={<SearchIcon />}
                                onClick={buscar}
                                disabled={cargando}
                            >
                                {cargando ? "Buscando..." : "Buscar"}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>

                {error ? (
                    <Alert severity="error" sx={{ mt: 1.5 }}>
                        {error}
                    </Alert>
                ) : null}

                {cargando ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : null}

                {!cargando && filas ? (
                    <Box sx={{ mt: 1.5 }}>
                        <Typography sx={{ mb: 0.75, fontSize: 13, fontWeight: 700 }}>
                            {filas.length} {filas.length === 1 ? "viaje encontrado" : "viajes encontrados"}
                            {filas.length > FILAS_VISTA ? (
                                <Box
                                    component="span"
                                    sx={{ ml: 0.5, fontWeight: 400, color: "text.secondary" }}
                                >
                                    (vista previa de los primeros {FILAS_VISTA}; el Excel incluye todos)
                                </Box>
                            ) : null}
                        </Typography>

                        {truncado ? (
                            <Alert severity="warning" sx={{ mb: 1 }}>
                                El reporte alcanzó el máximo de 20.000 viajes y puede estar incompleto. Acotá los filtros.
                            </Alert>
                        ) : null}

                        {filas.length === 0 ? (
                            <Alert severity="info">No hay viajes con esos filtros.</Alert>
                        ) : (
                            <TableContainer
                                sx={{
                                    maxHeight: 280,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 1,
                                }}
                            >
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            {COLUMNAS_VISTA.map((columna) => (
                                                <TableCell
                                                    key={columna.campo}
                                                    sx={{
                                                        py: 0.5,
                                                        fontSize: 11,
                                                        fontWeight: 700,
                                                        bgcolor: "#B2DFDB",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {columna.titulo}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filasVista.map((fila) => (
                                            <TableRow key={fila.id_viaje} hover>
                                                {COLUMNAS_VISTA.map((columna) => (
                                                    <TableCell
                                                        key={columna.campo}
                                                        sx={{ py: 0.25, fontSize: 12, whiteSpace: "nowrap" }}
                                                    >
                                                        {fila[columna.campo] ?? "-"}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                ) : null}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cerrar</Button>
                <Button
                    variant="contained"
                    color="success"
                    startIcon={<FileDownloadIcon />}
                    disabled={!filas || filas.length === 0 || cargando}
                    onClick={descargarExcel}
                >
                    Descargar Excel
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default DialogReporteViajes;